import { FunctionComponent, useMemo, useState } from 'react';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';
import CircularProgress from '@mui/material/CircularProgress';

import MapComponent from '../maps/Map';
import { SimpleObject } from '../../types';

// Capas de impacto: una sola seleccionable a la vez (NO se muestran las tres
// simultáneas en el mapa, según especificación del producto).
type CapaImpacto = 'tension' | 'lineas' | 'trafos';

/** Escenarios soportados por el mapa de impacto.
 *  - `snapshot_dmin` / `snapshot_dmax`: snapshots normativos.
 *  - `dia_verano` / `dia_invierno`: flujos diarios (NORM-007/008/012 §12.6).
 */
export type EscenarioMapa =
  | 'snapshot_dmin'
  | 'snapshot_dmax'
  | 'dia_verano'
  | 'dia_invierno';

export type ImpactoMapaPayload = {
  tension?: { nodos?: number[]; tramosIncidentes?: number[] };
  lineas?: { tramos?: number[] };
  trafos?: { transformadoresDx?: number[] };
} | null;

type Sets = {
  /** `tramo_id_origen` para resaltar como líneas (capa tensión: tramos
   *  incidentes a nodos afectados; capa líneas: tramos sobre-utilizados). */
  tramos: Set<number>;
  /** `transformador_dx_id_origen` para resaltar como puntos (capa trafos). */
  trafosDx: Set<number>;
};

type Props = {
  /** Payload del par (caso, escenario) ya seleccionado por el caller.
   *  - `null` o `undefined` → "no hay datos de impacto persistidos para este
   *    escenario/caso" (mensaje informativo).
   *  - Objeto vacío o con arrays vacíos → mensaje "no se introducen nuevos
   *    elementos en falla". */
  payload: ImpactoMapaPayload | null | undefined;
  /** Estado del mapa (features ya cargadas) compartido con otros bloques. */
  mapBaseState: {
    mapPointFeatures: Feature<Geometry, GeoJsonProperties>[];
    mapLineFeatures: Feature<Geometry, GeoJsonProperties>[];
    mapPolygonFeatures: Feature<Geometry, GeoJsonProperties>[];
    electricFeeders: SimpleObject;
    mapCenter: { lat: number; lng: number };
  };
  loading?: boolean;
  error?: string | null;
  /** Hint contextual (lo arma el caller con la copy específica de Fase 1 o
   *  Fase 2; el visor no decide la semántica del diff). */
  hint?: React.ReactNode;
};

// Paleta de impacto: contraste máximo entre afectados y no afectados.
const IMPACTO_COLOR = '#e53935';        // rojo brillante para líneas afectadas
const IMPACTO_LINE_WIDTH = 7;           // gruesas para destacar
const BASE_COLOR_ATENUADO = '#444444';  // gris oscuro para líneas sin impacto
const BASE_WIDTH_ATENUADO = 1.5;        // finas para no competir visualmente

function asNumber(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Extrae el `tramo.tramo_id_origen` de un feature `Linea`. La fuente de
 *  verdad es `properties.descripcionArtefacto.tramo_id` (string numérico,
 *  almacenado por `public.features`), que coincide con lo persistido en
 *  `datos_json.lineas.tramos` / `tramosIncidentes`. */
function extraerTramoId(feature: Feature<Geometry, GeoJsonProperties>): number | null {
  const p = feature.properties || {};
  const desc = (p.descripcionArtefacto || {}) as SimpleObject;
  return (
    asNumber(desc.tramo_id) ??
    asNumber(p.tramo_id) ??
    asNumber(desc.tramo_id_origen) ??
    asNumber(p.id)
  );
}

/** Extrae el `transformador_dx_id_origen` de un feature `Transformador`. */
function extraerTrafoDxId(feature: Feature<Geometry, GeoJsonProperties>): number | null {
  const p = feature.properties || {};
  const desc = (p.descripcionArtefacto || {}) as SimpleObject;
  const artefacto = String(p.artefacto || desc.artefacto || '').toLowerCase();
  if (!artefacto.includes('transformador')) return null;
  return (
    asNumber(desc.transformador_dx_id_origen) ??
    asNumber(desc.transformador_dx_id) ??
    asNumber(desc.identificador_del_transformador) ??
    asNumber(p.transformador_dx_id_origen) ??
    asNumber(p.transformador_dx_id) ??
    asNumber(p.id)
  );
}

const MapaImpactoVisor: FunctionComponent<Props> = ({
  payload,
  mapBaseState,
  loading = false,
  error = null,
  hint
}) => {
  const [capa, setCapa] = useState<CapaImpacto>('tension');

  const sets = useMemo<Sets>(() => {
    const empty: Sets = { tramos: new Set<number>(), trafosDx: new Set<number>() };
    if (!payload) return empty;

    const tramos = new Set<number>();
    if (capa === 'tension') {
      for (const t of payload.tension?.tramosIncidentes || []) tramos.add(Number(t));
    } else if (capa === 'lineas') {
      for (const t of payload.lineas?.tramos || []) tramos.add(Number(t));
    }

    const trafosDx = new Set<number>();
    if (capa === 'trafos') {
      for (const id of payload.trafos?.transformadoresDx || []) trafosDx.add(Number(id));
    }
    return { tramos, trafosDx };
  }, [payload, capa]);

  // Atenuar SIEMPRE todas las líneas (gris/finas) y resaltar solo las afectadas
  // (rojo/gruesas). Sin ramas condicionales por payload: el styling de impacto
  // tiene precedencia absoluta sobre el color por alimentador asignado por
  // backend (`getColorById` en `backend/utils/mapColors.ts`), incluso cuando
  // no hay datos de impacto persistidos para el caso/escenario seleccionado.
  // Esto evita que el mapa de resultados muestre, aunque sea brevemente o por
  // un cambio de escenario sin datos, los colores azul/naranja propios de la
  // página de configuración del estudio.
  const styledLineFeatures = useMemo(() => {
    if (!mapBaseState.mapLineFeatures.length) return mapBaseState.mapLineFeatures;
    return mapBaseState.mapLineFeatures.map((feature) => {
      const tramoId = extraerTramoId(feature);
      const esAfectada = tramoId !== null && sets.tramos.has(tramoId);
      return {
        ...feature,
        properties: {
          ...feature.properties,
          color: esAfectada ? IMPACTO_COLOR : BASE_COLOR_ATENUADO,
          lineWidth: esAfectada ? IMPACTO_LINE_WIDTH : BASE_WIDTH_ATENUADO
        }
      };
    });
  }, [mapBaseState.mapLineFeatures, sets.tramos]);

  const styledPointFeatures = useMemo(() => {
    if (!mapBaseState.mapPointFeatures.length) return mapBaseState.mapPointFeatures;
    if (sets.trafosDx.size === 0) return mapBaseState.mapPointFeatures;
    return mapBaseState.mapPointFeatures.map((feature) => {
      const trafoId = extraerTrafoDxId(feature);
      if (trafoId !== null && sets.trafosDx.has(trafoId)) {
        return {
          ...feature,
          properties: {
            ...feature.properties,
            // `impactoSnapshot: true` activa la capa `impacto-snapshot-trafo-ring`
            // (aro rojo) definida en mapGeoJSONUtils. El id de la capa MapLibre
            // es legacy (anterior a NORM-038) pero aplica también a los días
            // y a la Fase 2 (mitigaciones).
            impactoSnapshot: true
          }
        };
      }
      return feature;
    });
  }, [mapBaseState.mapPointFeatures, sets.trafosDx]);

  const totalDestacados =
    capa === 'trafos' ? sets.trafosDx.size : sets.tramos.size;

  const sinDatosImpacto = !loading && !error && payload == null;

  return (
    <div className='ecg-mapa-impacto-container'>
      {hint != null && (
        <div className='ecg-hint' style={{ marginBottom: '0.75rem' }}>
          {hint}
        </div>
      )}

      <div
        className='ecg-mapa-impacto-selector'
        role='radiogroup'
        aria-label='Capa de impacto a visualizar'>
        {(
          [
            { id: 'tension', label: 'Tensión (tramos asociados a nodos afectados)' },
            { id: 'lineas', label: 'Líneas con sobreutilización' },
            { id: 'trafos', label: 'Transformadores con sobreutilización' }
          ] as { id: CapaImpacto; label: string }[]
        ).map((opt) => (
          <button
            key={opt.id}
            type='button'
            role='radio'
            aria-checked={capa === opt.id}
            className={`ecg-mapa-impacto-tab ${capa === opt.id ? 'is-active' : ''}`}
            onClick={() => setCapa(opt.id)}>
            {opt.label}
          </button>
        ))}
      </div>

      {error && (
        <p className='ecg-hint' style={{ color: 'var(--vdr-danger)' }}>{error}</p>
      )}

      {sinDatosImpacto && (
        <p className='ecg-hint'>
          No hay datos de impacto persistidos para este caso/escenario (puede tratarse de un estudio
          ejecutado antes de habilitarse esta vista).
        </p>
      )}

      {!sinDatosImpacto && !error && totalDestacados === 0 && !loading && (
        <p className='ecg-hint'>
          No se introducen nuevos elementos en falla para esta capa.
        </p>
      )}

      <div
        className='ecg-mapa-impacto-frame'
        style={{ position: 'relative', height: 460 }}>
        <MapComponent
          mapPointFeatures={styledPointFeatures}
          mapLineFeatures={styledLineFeatures}
          mapPolygonFeatures={mapBaseState.mapPolygonFeatures}
          electricFeeders={mapBaseState.electricFeeders}
          mapCenter={mapBaseState.mapCenter}
          mapZoom={15.5}
        />
        {loading && (
          <div className='ecg-map-loader'>
            <CircularProgress />
          </div>
        )}
      </div>
    </div>
  );
};

export default MapaImpactoVisor;
